import React from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { APP_NAME } from '@/config/appConfig';
import Button from '@components/common/atoms/AppButton';
import Icon from '@components/common/atoms/Icon';
import SectionEyebrow from './SectionEyebrow';
import { BENEFITS } from '../../constants/landingPageConstants';

const LandingBenefits: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const primaryCtaLabel = isAuthenticated ? 'Go to Dashboard' : 'Sign In';
  const goToApp = () => navigate(isAuthenticated ? '/dashboard' : '/login');

  return (
    <Box component="section" id="benefits" className="bg-slate-50 dark:bg-slate-900/40">
      <Box className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <Box className="grid items-start gap-12 lg:grid-cols-12">
          <Box className="lg:col-span-4 lg:sticky lg:top-28">
            <SectionEyebrow>Why choose {APP_NAME}</SectionEyebrow>
            <Typography component="h2" className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
              Built to save time and elevate your organisation
            </Typography>
            <Typography component="p" className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              Ditch the spreadsheets and disconnected tools. {APP_NAME} gives HR teams and
              employees a faster, clearer and more secure way to work together.
            </Typography>
            <Button variant="primary" size="lg" onClick={goToApp} className="mt-8">
              <Icon name="LogIn" size={18} className="mr-2" />
              {primaryCtaLabel}
            </Button>
          </Box>

          <Box className="grid gap-6 sm:grid-cols-2 lg:col-span-8">
            {BENEFITS.map((benefit) => (
              <Box
                key={benefit.title}
                className="rounded-2xl border border-slate-200/80 bg-white p-6 dark:border-slate-800/80 dark:bg-slate-900"
              >
                <Box className="flex items-center gap-3">
                  <Box className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
                    <Icon name={benefit.icon} size={20} />
                  </Box>
                  <Typography component="h3" className="text-base font-bold">{benefit.title}</Typography>
                </Box>
                <Typography component="p" className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {benefit.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingBenefits;
